import { ShopifyUrlInput } from '@/components/ConnectShopify';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { authService } from '@common/api/authService';
import { Button } from '@common/components/Button';
import Icon from '@common/components/Icon';
import { AlertDialog } from '@common/index';
import { useState } from 'react';
import cardStyles from './IntegrationCard.module.scss';
import styles from './ShopifyIntegration.module.scss';

interface ShopifyIntegrationProps {
    connectedStore?: any;
    onConnect?: () => void;
    onDisconnect?: () => void;
}

export default function ShopifyIntegration({
    connectedStore,
    onConnect,
    onDisconnect
}: ShopifyIntegrationProps) {
    const { profile, setProfile } = useAuth();
    const { showToast } = useToast();
    const [shopifyUrl, setShopifyUrl] = useState('');
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');

    const handleConnect = () => {
        if (!shopifyUrl) {
            setError('Please enter your Shopify store url');
            return;
        }
        setIsConnecting(true);
        (async () => {
            try {
                const url = new URL(window.location.href);
                url.searchParams.set('setting_modal', 'integration');
                const res = await authService.connectShopify({
                    shop_domain: `${shopifyUrl}.myshopify.com`,
                    redirect_url: url.toString(),
                });

                if (!res.authorizationUrl) {
                    setIsConnecting(false);
                    setError('Shopify store is not found. Please check the store url.');
                    return;
                }

                if (res.authorizationUrl) {
                    window.location.href = res.authorizationUrl;
                }
            } catch (error) {
                console.error('连接 Shopify 失败:', error);
                setIsConnecting(false);
                setError('Failed to connect to Shopify store. Please try again.');
            }
        })();

        onConnect?.();
    };

    const handleDisconnect = () => {
        setIsConnecting(true);
        (async () => {
            try {
                const res = await authService.disconnectShopify({shop_domain: `${connectedStore?.domain}`,});

                if (!res.success) {
                    showToast('Failed to disconnect Shopify store. Please try again.', 'error');
                } else {
                    const _profile = { ...profile };
                    _profile.shopInfos = _profile.shopInfos?.map((item: any) =>
                        item.domain === connectedStore?.domain ? {
                            ...item,
                            physicFlag: 0,
                        } : item) || [];
                    setProfile(_profile as any);
                    showToast('Shopify store disconnected, pulses are paused.', 'success');
                }
            } catch (error) {
                console.log('error', error);
                showToast('Failed to disconnect Shopify store. Please try again.', 'error');
            } finally {
                setIsConnecting(false);
            }
        })();

        onDisconnect?.();
    };

    return (
        <div className={cardStyles.integrationCard}>
            <div className={cardStyles.cardHeader}>
                <Icon name="shopify" size={24} />
                <h3 className={cardStyles.cardTitle}>Shopify</h3>
            </div>

            <p className={cardStyles.cardDescription}>
                Connect your store to personalize your Bustly experience.
                By syncing your basic store info, we can provide more relevant
                market insights and product recommendations.
            </p>

            {connectedStore ? (
                <div className={styles.connectedSection}>
                    <div className={styles.shopInfo}>
                        <div className={styles.shopDetails}>
                            <div className={styles.shopHeader}>
                                <span className={styles.shopName}>Store URL:</span>
                                <div className={styles.shopUrl}>{connectedStore?.domain}</div>
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex', gap: 16, alignItems: 'center'
                            }}
                        >
                            <span className={styles.shopStatus}>
                                <b style={{ fontSize: 20, marginRight: 4 }}>·</b>
                                Connected
                            </span>
                            <AlertDialog
                                title="Before you disconnect…"
                                subtitle="If you disconnect, Bustly will stop syncing your store.
                                Real-time Pulse alerts and insights will pause. You can reconnect anytime.
                                This won't delete past data unless you choose to remove it."
                                confirmText="Disconnect Store"
                                onConfirm={() => handleDisconnect()}
                                cancelText="Keep Connected"
                            >
                                <Button
                                    variant="outline"
                                    size="2"
                                    className={styles.disconnectButton}
                                    loading={isConnecting}
                                    color="gray"
                                    style={{ padding: '0px 40px' }}
                                >
                                    Disconnect
                                </Button>
                            </AlertDialog>
                        </div>
                    </div>
                    {error ? <div className={styles.errorMessage}>{error}</div> : null}
                </div>
            ) : (
                <div className={styles.urlInputSection}>
                    <ShopifyUrlInput
                        value={shopifyUrl}
                        showIcon={false}
                        onChange={(value) => setShopifyUrl(value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleConnect();
                            }
                        }}
                        error={error}
                    />
                    {error ? <div className={styles.errorMessage}>{error}</div> : null}
                    <Button
                        className={styles.connectButton}
                        onClick={() => handleConnect()}
                        loading={isConnecting}
                        size="2"
                        variant="solid"
                        color="primary"
                        style={{ padding: '0px 40px', marginTop: 16 }}
                    >
                        Connect
                    </Button>
                </div>
            )}
        </div>
    );
}